<script lang="ts">
import { computed, defineComponent, ref } from '@vue/composition-api';
import { commandDispatcher, queryExecutor } from '@/_core/cqrs';
import { snackbar } from '@/_core/app-components/use';
import { CloneExampleStorymapToWorkspace } from '@/api/workspaces/commands';
import {
    ExampleStorymapCloneTargetsQuery,
    ExampleStorymapCloneTargetProductModel,
    ExampleStorymapCloneTargetWorkspaceModel,
} from '@/api/workspaces/queries';

interface CloneTargetOption {
    workspace: ExampleStorymapCloneTargetWorkspaceModel;
    product: ExampleStorymapCloneTargetProductModel;
}

const PublishedRecipientAppBar = defineComponent({
    name: 'PublishedRecipientAppBar',

    setup() {
        const publicAccessKind = 0;
        const privateLinkAccessKind = 1;
        const cloneTargets = ref<ExampleStorymapCloneTargetWorkspaceModel[]>([]);
        const cloneTargetsLoaded = ref(false);
        const isCloneInProgress = ref(false);
        const isClonePickerOpen = ref(false);
        const bootstrap = computed(() => initAppData.publishedStorymapBootstrap);
        const signupUrl = computed(() => {
            const url = new URL('signup', initAppData.appUrls.rootUrl);
            url.searchParams.set('utm_source', 'public-storymap');
            url.searchParams.set('utm_campaign', bootstrap.value?.accessKey || '');

            if (bootstrap.value?.isExampleWorkspaceMap) {
                url.searchParams.set('example', bootstrap.value.accessKey);
            }

            return url.toString();
        });
        const privateLoginUrl = computed(() => {
            const url = new URL(initAppData.appUrls.loginUrl);
            url.searchParams.set('r', window.location.href);
            return url.toString();
        });
        const isAuthenticated = computed(() => !!bootstrap.value?.isAuthenticated);
        const isPublic = computed(() => Number(bootstrap.value?.accessKind) === publicAccessKind);
        const isPrivateLink = computed(() => Number(bootstrap.value?.accessKind) === privateLinkAccessKind);
        const showExampleSignup = computed(() => isPublic.value && !!bootstrap.value?.isExampleWorkspaceMap && !isAuthenticated.value);
        const showPublicSignupLogin = computed(() => isPublic.value && !bootstrap.value?.isExampleWorkspaceMap && !isAuthenticated.value);
        const showPrivateLogin = computed(() => isPrivateLink.value && !isAuthenticated.value);
        const showExampleClone = computed(() => isPublic.value && !!bootstrap.value?.isExampleWorkspaceMap && isAuthenticated.value);
        const cloneTargetOptions = computed<CloneTargetOption[]>(() =>
            cloneTargets.value.reduce((options, workspace) => {
                workspace.Products.forEach((product) => {
                    options.push({ workspace, product });
                });
                return options;
            }, [] as CloneTargetOption[])
        );

        const loadCloneTargets = async () => {
            if (cloneTargetsLoaded.value) {
                return;
            }

            const result = await queryExecutor.executeQuery(new ExampleStorymapCloneTargetsQuery());
            cloneTargets.value = result.Workspaces || [];
            cloneTargetsLoaded.value = true;
        };

        const cloneToTarget = async (target: CloneTargetOption) => {
            if (!bootstrap.value?.accessKey || isCloneInProgress.value) {
                return;
            }

            isClonePickerOpen.value = false;
            isCloneInProgress.value = true;
            try {
                const result = await commandDispatcher.dispatchCommand(
                    new CloneExampleStorymapToWorkspace(bootstrap.value.accessKey, target.workspace.WorkspaceId, target.product.ProductId)
                );
                if (result.IsSuccess) {
                    window.open(result.Url, '_blank', 'noopener');
                } else {
                    snackbar.showError(result.Message || 'The map could not be cloned.');
                }
            } catch (error) {
                snackbar.showError('The map could not be cloned.');
            } finally {
                isCloneInProgress.value = false;
            }
        };

        const onCloneButtonClick = async () => {
            try {
                await loadCloneTargets();
                if (cloneTargetOptions.value.length === 0) {
                    isClonePickerOpen.value = false;
                    snackbar.showError('No eligible workspace was found.');
                    return;
                }

                if (cloneTargetOptions.value.length === 1) {
                    await cloneToTarget(cloneTargetOptions.value[0]);
                    return;
                }

                isClonePickerOpen.value = true;
            } catch (error) {
                isClonePickerOpen.value = false;
                snackbar.showError('Clone targets could not be loaded.');
            }
        };

        const cancelClonePicker = () => {
            isClonePickerOpen.value = false;
        };

        return {
            cancelClonePicker,
            cloneTargetOptions,
            cloneToTarget,
            isCloneInProgress,
            isClonePickerOpen,
            loginUrl: initAppData.appUrls.loginUrl,
            onCloneButtonClick,
            privateLoginUrl,
            showExampleClone,
            showExampleSignup,
            showPrivateLogin,
            showPublicSignupLogin,
            signupUrl,
        };
    },
});

export default PublishedRecipientAppBar;
</script>

<template>
  <v-app-bar
    app
    flat
    class="px-2"
    color="blueGrey05Light"
  >
    <v-toolbar-title class="blueGrey90Light--text font-weight-bold text-h5" style="line-height: 1.333; min-width: 0;">
      <portal-target name="header-title" />
    </v-toolbar-title>

    <v-spacer />
    <portal-target name="header-center" />
    <v-spacer />

    <div class="recipient-auth-region">
      <div v-if="showExampleSignup" class="recipient-auth-region__example">
        <span class="recipient-auth-region__caption">
          Sign up to create your own product plan from this map.
        </span>
        <v-btn
          color="primary"
          small
          depressed
          :href="signupUrl"
          target="_blank"
          rel="noopener"
          data-testid="published-recipient-use-map"
        >
          Use this map
        </v-btn>
      </div>

      <div v-else-if="showPublicSignupLogin" class="recipient-auth-region__buttons">
        <v-btn
          color="primary"
          small
          depressed
          :href="signupUrl"
          target="_blank"
          rel="noopener"
          data-testid="published-recipient-signup"
        >
          Sign up
        </v-btn>
        <v-btn
          color="secondary"
          small
          text
          :href="loginUrl"
          target="_blank"
          rel="noopener"
          data-testid="published-recipient-login"
        >
          Log in
        </v-btn>
      </div>

      <v-btn
        v-else-if="showPrivateLogin"
        color="primary"
        small
        depressed
        :href="privateLoginUrl"
        target="_blank"
        rel="noopener"
        data-testid="published-recipient-private-login"
      >
        Log in
      </v-btn>

      <div v-else-if="showExampleClone" class="recipient-auth-region__clone">
        <v-menu
          v-model="isClonePickerOpen"
          offset-y
          left
          max-width="320"
          min-width="320"
          :close-on-content-click="false"
        >
          <template #activator="{ on, attrs }">
            <v-btn
              color="primary"
              small
              depressed
              :loading="isCloneInProgress"
              :disabled="isCloneInProgress"
              data-testid="published-recipient-clone"
              v-bind="attrs"
              v-on="on"
              @click.stop="onCloneButtonClick"
            >
              Clone to my workspace
            </v-btn>
          </template>

          <v-list
            nav
            class="recipient-clone-picker"
            data-testid="published-recipient-clone-picker"
          >
            <v-list-item
              v-for="target in cloneTargetOptions"
              :key="`${target.workspace.WorkspaceId}-${target.product.ProductId}`"
              data-testid="published-recipient-clone-target"
              @click="cloneToTarget(target)"
            >
              <v-list-item-content>
                <v-list-item-title>{{ target.workspace.WorkspaceName }}</v-list-item-title>
                <span class="list-item-description">{{ target.product.ProductName }}</span>
              </v-list-item-content>
            </v-list-item>
            <v-divider />
            <v-list-item
              data-testid="published-recipient-clone-cancel"
              @click="cancelClonePicker"
            >
              <v-list-item-title>Cancel</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>
  </v-app-bar>
</template>

<style lang="scss" scoped>
@import '~vuetify/src/components/VList/_variables.scss';

::v-deep {
    .v-toolbar__content {
        padding-left: 8px;
        padding-right: 12px;
    }

    .recipient-clone-picker {
        .v-list-item__title {
            font-size: 14px;
            font-weight: 500;
        }

        .list-item-description {
            font-size: $list-item-dense-title-font-size;
        }
    }
}

.recipient-auth-region {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    min-width: 0;
    max-width: min(460px, 48vw);
    margin-left: 12px;
}

.recipient-auth-region__example,
.recipient-auth-region__buttons {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    min-width: 0;
}

.recipient-auth-region__clone {
    display: flex;
    align-items: center;
    justify-content: flex-end;
}

.recipient-auth-region__caption {
    overflow: hidden;
    color: var(--v-blueGrey70Light-base);
    font-size: 12px;
    line-height: 16px;
    text-align: right;
    text-overflow: ellipsis;
    white-space: nowrap;
}

@media (max-width: 720px) {
    .recipient-auth-region {
        max-width: 45vw;
        margin-left: 8px;
    }

    .recipient-auth-region__caption {
        display: none;
    }
}
</style>
